import React, { useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { useLenis } from '@studio-freight/react-lenis';

import { parse, stringify } from '../../../../util/urlHelpers';
import { useIntl } from '../../../../util/reactIntl';
import { IconArrowHead, NamedLink } from '../../../../components';

import Field from '../../Field';
import BlockContainer from '../../BlockBuilder/BlockContainer';

import css from './BlockMediaCards.module.css';
import innerPageCss from '../SectionContainer/InnerPage.module.css';

const MEDIA_ITEMS_PER_PAGE = 6;
const PAGE_WINDOW_SIZE = 5;
const MEDIA_CARDS_SCROLL_ID = 'media-cards';

const getTopbarOffset = () => {
  if (typeof window === 'undefined') {
    return 72;
  }
  const styles = window.getComputedStyle(document.documentElement);
  const token = window.matchMedia('(min-width: 768px)').matches
    ? '--topbarHeightDesktop'
    : '--topbarHeight';
  return Number.parseFloat(styles.getPropertyValue(token)) || 72;
};

const scrollToMediaCards = lenis => {
  const element = document.getElementById(MEDIA_CARDS_SCROLL_ID);
  if (!element) {
    return;
  }

  const offset = -(getTopbarOffset() + 24);

  if (lenis) {
    lenis.scrollTo(element, {
      offset,
      duration: 1.2,
      immediate: false,
      force: true,
    });
    return;
  }

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const getVisiblePages = (page, totalPages) => {
  if (totalPages <= PAGE_WINDOW_SIZE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(PAGE_WINDOW_SIZE / 2);
  let start = Math.max(1, page - half);
  let end = start + PAGE_WINDOW_SIZE - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - PAGE_WINDOW_SIZE + 1);
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Custom PageBuilder block that renders collected CMS media cards with pagination.
 *
 * @component
 * @param {Object} props
 * @param {string} props.blockId
 * @param {Array<Object>} props.cards collected media card field groups
 * @param {Object} props.options PageBuilder field options
 * @param {Object} props.location react-router location
 * @returns {JSX.Element}
 */
const BlockMediaCardsComponent = props => {
  const { blockId, cards = [], options, location } = props;
  const intl = useIntl();
  const lenis = useLenis();
  const lenisRef = useRef(lenis);
  const fieldOptions = { fieldComponents: options?.fieldComponents };
  lenisRef.current = lenis;

  const query = parse(location?.search);
  const requestedPage = Math.max(1, Number.parseInt(query.page, 10) || 1);
  const totalItems = cards.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / MEDIA_ITEMS_PER_PAGE));
  const page = Math.min(requestedPage, totalPages);
  const startIndex = (page - 1) * MEDIA_ITEMS_PER_PAGE;
  const visibleCards = cards.slice(startIndex, startIndex + MEDIA_ITEMS_PER_PAGE);
  const showPagination = totalItems > MEDIA_ITEMS_PER_PAGE;
  const visiblePages = getVisiblePages(page, totalPages);
  const prevPage = page > 1 ? page - 1 : null;
  const nextPage = page < totalPages ? page + 1 : null;

  useEffect(() => {
    const hasPageParam = Object.prototype.hasOwnProperty.call(parse(location?.search), 'page');
    if (!hasPageParam) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      scrollToMediaCards(lenisRef.current);
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [page, location?.search]);

  const pageLocation = pageNumber => ({
    search: stringify({ ...query, page: pageNumber }),
    hash: MEDIA_CARDS_SCROLL_ID,
  });

  const onPagerClick = () => {
    window.setTimeout(() => {
      scrollToMediaCards(lenisRef.current);
    }, 150);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <BlockContainer
      id={MEDIA_CARDS_SCROLL_ID}
      className={classNames(css.root, innerPageCss.mediaPage)}
    >
      <div className={css.list}>
        {visibleCards.map(card => (
          <article className={css.card} key={card.id}>
            {card.coverMedia ? (
              <div className={css.cover}>
                <Field
                  data={card.coverMedia}
                  sizes="(max-width: 767px) 100vw, 260px"
                  options={fieldOptions}
                />
              </div>
            ) : null}
            <div className={css.content}>
              {card.logoMedia ? (
                <div className={css.logo}>
                  <Field data={card.logoMedia} sizes="80px" options={fieldOptions} />
                </div>
              ) : null}
              <div className={css.copy}>
                <Field data={card.text} className={css.date} options={fieldOptions} />
                <Field data={card.title} className={css.title} options={fieldOptions} />
                <Field
                  data={card.callToAction}
                  className={css.readMore}
                  options={fieldOptions}
                />
              </div>
            </div>
          </article>
        ))}
      </div>

      {showPagination ? (
        <nav
          className={css.pagination}
          aria-label={intl.formatMessage({ id: 'MediaPage.navLabel' })}
        >
          <div className={css.pageList}>
            {visiblePages.map(pageNumber => {
              const isCurrent = pageNumber === page;
              return isCurrent ? (
                <span
                  key={pageNumber}
                  className={classNames(css.pageNumber, css.pageNumberCurrent)}
                  aria-current="page"
                >
                  {pageNumber}
                </span>
              ) : (
                <span key={pageNumber} className={css.pagerHit} onClick={onPagerClick}>
                  <NamedLink
                    className={css.pageNumber}
                    name="CMSPage"
                    params={{ pageId: 'media' }}
                    to={pageLocation(pageNumber)}
                    title={intl.formatMessage({ id: 'MediaPage.toPage' }, { page: pageNumber })}
                  >
                    {pageNumber}
                  </NamedLink>
                </span>
              );
            })}
          </div>

          <div className={css.pagerControls}>
            {prevPage ? (
              <span className={css.pagerHit} onClick={onPagerClick}>
                <NamedLink
                  className={classNames(css.pagerButton, css.pagerPrev)}
                  name="CMSPage"
                  params={{ pageId: 'media' }}
                  to={pageLocation(prevPage)}
                  title={intl.formatMessage({ id: 'MediaPage.prev' })}
                >
                  <IconArrowHead direction="left" size="small" rootClassName={css.pagerIcon} />
                  <span>{intl.formatMessage({ id: 'MediaPage.prev' })}</span>
                </NamedLink>
              </span>
            ) : (
              <span className={classNames(css.pagerButton, css.pagerPrev, css.pagerButtonDisabled)}>
                <IconArrowHead direction="left" size="small" rootClassName={css.pagerIcon} />
                <span>{intl.formatMessage({ id: 'MediaPage.prev' })}</span>
              </span>
            )}

            {nextPage ? (
              <span className={css.pagerHit} onClick={onPagerClick}>
                <NamedLink
                  className={classNames(css.pagerButton, css.pagerNext)}
                  name="CMSPage"
                  params={{ pageId: 'media' }}
                  to={pageLocation(nextPage)}
                  title={intl.formatMessage({ id: 'MediaPage.next' })}
                >
                  <span>{intl.formatMessage({ id: 'MediaPage.next' })}</span>
                  <IconArrowHead direction="right" size="small" rootClassName={css.pagerIcon} />
                </NamedLink>
              </span>
            ) : (
              <span className={classNames(css.pagerButton, css.pagerNext, css.pagerButtonDisabled)}>
                <span>{intl.formatMessage({ id: 'MediaPage.next' })}</span>
                <IconArrowHead direction="right" size="small" rootClassName={css.pagerIcon} />
              </span>
            )}
          </div>
        </nav>
      ) : null}
    </BlockContainer>
  );
};

const BlockMediaCards = withRouter(BlockMediaCardsComponent);

export default BlockMediaCards;
export { MEDIA_ITEMS_PER_PAGE };
